import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';
import { IbdEvents } from '@theme/ibd-events';
import {
  RATINGS_WIDGET_DEFAULT_SETTINGS,
  RATINGS_WIDGET_SCRIPT_URL,
  RATINGS_WIDGET_CSS_URL,
} from '@theme/ibd-reviewsio-settings';
/** @typedef {import('@shopify/events').ProductSelectEvent} ProductSelectEvent */
/** @typedef {import('@theme/ibd-utilities').IbdSkeleton} IbdSkeleton */
/** @typedef {import('@theme/ibd-events').IbdProductUpdateEvent} IbdProductUpdateEvent */
/** @typedef {import('@theme/ibd-reviewsio-types').RatingsWidgetSettings} RatingsWidgetSettings */

/** Delay between retries while waiting for the Reviews.io script to load. */
const SCRIPT_RETRY_DELAY_MS = 200;

/** Debounce window that collapses refresh triggers (morph updatedCallback + event handlers). */
const REFRESH_DEBOUNCE_MS = 50;

/**
 * Max time to wait for Reviews.io to finish before giving up and hiding the skeleton
 * (e.g. the script or API is blocked by an ad-blocker, or the request failed).
 */
const LOAD_FALLBACK_TIMEOUT_MS = 15000;

/**
 * How long a refresh must be in flight before the skeleton is shown.
 * Faster refreshes keep the current visual state, so e.g. switching between
 * two 0-rating variants doesn't blink the skeleton on and straight off again.
 */
const SHIMMER_DELAY_MS = 300;

/**
 * Shared injection of the Reviews.io rating-snippet assets.
 * Fired as soon as the first `<ibd-product-ratings>` connects — PDP stars are
 * above the fold, so waiting on interaction left the skeleton shimmering.
 * Module-level so every component instance (product page + cards) shares one
 * injection.
 * @type {Promise<void>|null}
 */
let ratingAssetsPromise = null;

/**
 * Inserts the Reviews.io rating-snippet stylesheet and script once.
 * @returns {void}
 */
function injectRatingAssets() {
  if (!document.querySelector(`link[href="${RATINGS_WIDGET_CSS_URL}"]`)) {
    const link = document.createElement('link');
    link.href = RATINGS_WIDGET_CSS_URL;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }

  if (!document.querySelector(`script[src="${RATINGS_WIDGET_SCRIPT_URL}"]`)) {
    const script = document.createElement('script');
    script.src = RATINGS_WIDGET_SCRIPT_URL;
    script.defer = true;
    document.head.appendChild(script);
  }
}

/**
 * @returns {Promise<void>} Resolves once the Reviews.io script and stylesheet tags exist.
 */
function ensureRatingAssetsInjected() {
  ratingAssetsPromise ??= Promise.resolve(injectRatingAssets());

  return ratingAssetsPromise;
}

/**
 * Reviews.io bookkeeping attributes set on the widget container by dist.js.
 * They must be cleared before a reload: the script skips any element with
 * `data-retrieved`, and the response handler matches on `rating-snippet-batch`.
 */
const REVIEWSIO_WIDGET_ATTRIBUTES = [
  'data-retrieved',
  'data-done',
  'data-pending',
  'rating-snippet-batch',
  'aria-hidden',
  'aria-label',
  'title',
  'role',
  'tabindex',
];

/**
 * IBD Product Ratings Component
 *
 * Displays Reviews.io rating snippets for products.
 *
 * Reviews.io signals completion on the widget container with `data-done="true"`,
 * EXCEPT when a product has no reviews and `showEmptyStars` is false — then it
 * only sets `aria-hidden="true"`. Both signals must be observed.
 * Full lifecycle: .ibd/references/ibd-reviewsio-rating-snippet.md
 */
class IbdProductRatings extends Component {
  /** @type {MutationObserver|null} */
  widgetObserver = null;

  /** @type {number} */
  maxAttempts = 50;

  /** @type {number} */
  attempts = 0;

  /** @type {number|null} */
  timeout = null;

  /** @type {number|null} */
  fallbackTimeout = null;

  /** @type {number|null} */
  refreshTimeout = null;

  /** @type {number|null} */
  shimmerDelayTimeout = null;

  /** @type {RatingsWidgetSettings} */
  settings = RATINGS_WIDGET_DEFAULT_SETTINGS;

  /** @type {boolean} */
  widgetLoaded = false;

  /** @type {IbdSkeleton|null} */
  #skeleton = null;

  /** @type {Document|null} */
  #pendingRefreshHtml = null;

  /**
   * Caches the skeleton child before `connectedCallback` runs.
   */
  constructor() {
    super();
    this.#skeleton = this.querySelector('ibd-skeleton');
  }

  /**
   * Arms load, skeleton, and event listeners when the element enters the DOM.
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();

    this.#reset();
    this.#refreshSkeleton();
    void this.#startLoadingDeferred();
    this.#bindEventListeners();
    this.#bindReviewsAnchor();
  }

  /**
   * Starts the load pipeline only after the shared Reviews.io assets have been
   * requested, so the retry and fallback windows in #startLoading don't start
   * counting down before the script can possibly arrive.
   * @returns {Promise<void>}
   */
  async #startLoadingDeferred() {
    await ensureRatingAssetsInjected();
    if (!this.isConnected) return;
    this.#startLoading();
  }

  /**
   * Called when the element is updated via morph.
   *
   * A morph can reset the widget container to its server-rendered state (e.g.
   * product-card.js morphs this element after an in-card variant change), and
   * nothing else re-triggers Reviews.io for that case — so detect the wipe
   * (class restored, bookkeeping attributes stripped) and reload.
   * @returns {void}
   */
  updatedCallback() {
    super.updatedCallback();
    this.#refreshSkeleton();

    const widgetContainer = this.#getWidgetContainer();
    if (!widgetContainer) return;

    const wasReset =
      widgetContainer.classList.contains('ruk_rating_snippet') &&
      !widgetContainer.hasAttribute('data-done') &&
      !widgetContainer.hasAttribute('data-retrieved') &&
      !widgetContainer.hasAttribute('data-pending');

    if (wasReset) this.#scheduleRefresh();
  }

  /**
   * Tears down timers, observers, and listeners when the element leaves the DOM.
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reset();
    this.#unbindEventListeners();
    this.#unbindReviewsAnchor();
  }

  /**
   * Returns the widget container. Queried by the stable data attribute, never by
   * the `.ruk_rating_snippet` class, which `#freezeWidget()` removes after load.
   *
   * @returns {HTMLElement|null}
   */
  #getWidgetContainer() {
    return this.querySelector('[data-product-ratings-widget]');
  }

  /**
   * Parses and merges the settings from the `data-settings` attribute.
   * `null`/`undefined` values are dropped so a render without block settings
   * (e.g. the product-card Section Rendering response) can't clobber defaults.
   *
   * @returns {boolean} Whether valid settings (including a store) were loaded.
   */
  #loadSettings() {
    let settings = this.settings;

    try {
      const parsed = JSON.parse(this.dataset.settings ?? '{}');
      const customSettings = Object.fromEntries(
        Object.entries(parsed).filter(([, value]) => value !== null && value !== undefined)
      );
      settings = { ...this.settings, ...customSettings };
    } catch (error) {
      console.error('IBD | Product Ratings | #loadSettings | Error parsing settings', error);
    }

    if (!('store' in settings) || !settings.store) return false;

    this.settings = settings;
    return true;
  }

  /**
   * Starts (or restarts) the full load pipeline: settings, fallback timer,
   * widget observation and the Reviews.io snippet call.
   * @returns {void}
   */
  #startLoading() {
    if (!this.#loadSettings()) {
      this.#failSoft('Invalid settings (no store); skipping widget load');
      return;
    }

    this.#startFallbackTimer();
    this.#observeWidget();
    this.#loadRatingSnippet();
  }

  /**
   * Invokes `window.ratingSnippet` once the Reviews.io script is present.
   * Retries until `maxAttempts` if the global is not ready yet.
   * @returns {void}
   */
  #loadRatingSnippet() {
    this.attempts++;

    // Check for ratingSnippet function (loaded from external script)
    // @ts-ignore - ratingSnippet is loaded from external script
    const ratingSnippetFn = window.ratingSnippet || (typeof ratingSnippet !== 'undefined' ? ratingSnippet : null);

    if (typeof ratingSnippetFn === 'function') {
      if (this.timeout) clearTimeout(this.timeout);
      ratingSnippetFn('ruk_rating_snippet', this.settings);
    } else if (this.attempts < this.maxAttempts) {
      this.timeout = setTimeout(() => this.#loadRatingSnippet(), SCRIPT_RETRY_DELAY_MS);
    } else {
      this.#failSoft(`Reviews.io script did not load after ${this.maxAttempts} attempts`);
    }
  }

  /**
   * Observes the widget container to detect when Reviews.io finishes processing it.
   * Watches both `data-done` (loaded with content) and `aria-hidden` (no reviews
   * while `showEmptyStars` is false — Reviews.io never sets `data-done` then).
   * @returns {void}
   */
  #observeWidget() {
    const widgetContainer = this.#getWidgetContainer();
    if (!widgetContainer) {
      this.#failSoft('Widget container not found');
      return;
    }

    this.#disconnectWidgetObserver();

    this.widgetObserver = new MutationObserver(() => this.#checkWidgetState(widgetContainer));
    this.widgetObserver.observe(widgetContainer, {
      attributes: true,
      attributeFilter: ['data-done', 'aria-hidden'],
    });

    // Also check the current state (in case the widget loaded before the observer attached)
    this.#checkWidgetState(widgetContainer);
  }

  /**
   * Completes the load when Reviews.io has finished with the widget container.
   *
   * @param {HTMLElement} widgetContainer - The widget container element.
   * @returns {void}
   */
  #checkWidgetState(widgetContainer) {
    if (this.widgetLoaded) return;

    if (widgetContainer.getAttribute('data-done') === 'true') {
      this.#completeLoading(true);
    } else if (widgetContainer.getAttribute('aria-hidden') === 'true') {
      this.#completeLoading(false);
    }
  }

  /**
   * Marks the widget as loaded, resolves the skeleton and freezes the container.
   *
   * @param {boolean} hasReviews - Whether the product has reviews to display.
   * @returns {void}
   */
  #completeLoading(hasReviews) {
    this.widgetLoaded = true;
    this.#clearFallbackTimer();
    this.#clearShimmerDelay();
    this.toggleAttribute('data-no-reviews', !hasReviews);
    this.#skeleton?.resetShimmer();
    this.#freezeWidget();
  }

  /**
   * Gives up on loading: hides the skeleton and the component so no stuck shimmer
   * remains. The widget observer (when attached) stays alive, so if Reviews.io
   * completes late the component recovers via `#checkWidgetState`.
   *
   * @param {string} message - The reason for giving up.
   * @returns {void}
   */
  #failSoft(message) {
    console.warn(`IBD | Product Ratings | ${message}`);
    this.#clearFallbackTimer();
    this.#clearShimmerDelay();
    this.setAttribute('data-no-reviews', '');
    this.#skeleton?.resetShimmer();
  }

  /**
   * Shows the skeleton for an in-flight refresh, but only after a short delay.
   * Until then the component keeps its current visual state (previous stars, or
   * hidden via data-no-reviews), so fast refreshes never flash the skeleton.
   * If a morph already restored the server-rendered shimmer, it is kept as-is.
   * @returns {void}
   */
  #scheduleShimmer() {
    this.#clearShimmerDelay();

    if (this.#skeleton?.hasAttribute('ibd-shimmer')) {
      this.removeAttribute('data-no-reviews');
      return;
    }

    this.shimmerDelayTimeout = setTimeout(() => {
      this.shimmerDelayTimeout = null;
      if (this.widgetLoaded) return;
      this.removeAttribute('data-no-reviews');
      this.#skeleton?.shimmer();
    }, SHIMMER_DELAY_MS);
  }

  /**
   * Cancels a pending delayed-shimmer timeout.
   * @returns {void}
   */
  #clearShimmerDelay() {
    if (this.shimmerDelayTimeout) {
      clearTimeout(this.shimmerDelayTimeout);
      this.shimmerDelayTimeout = null;
    }
  }

  /**
   * Starts the give-up timer that hides a stuck skeleton if Reviews.io never finishes.
   * @returns {void}
   */
  #startFallbackTimer() {
    this.#clearFallbackTimer();
    this.fallbackTimeout = setTimeout(() => {
      if (this.widgetLoaded) return;
      this.#failSoft('Widget did not finish loading in time; hiding skeleton');
    }, LOAD_FALLBACK_TIMEOUT_MS);
  }

  /**
   * Cancels the give-up timer.
   * @returns {void}
   */
  #clearFallbackTimer() {
    if (this.fallbackTimeout) {
      clearTimeout(this.fallbackTimeout);
      this.fallbackTimeout = null;
    }
  }

  /**
   * Disconnects the widget observer.
   * @returns {void}
   */
  #disconnectWidgetObserver() {
    if (this.widgetObserver) {
      this.widgetObserver.disconnect();
      this.widgetObserver = null;
    }
  }

  /**
   * Refreshes the skeleton element reference.
   * Should be called after morph/updates to ensure we have the current skeleton element.
   * @returns {void}
   */
  #refreshSkeleton() {
    /** @type {IbdSkeleton|null} */
    this.#skeleton = this.querySelector('ibd-skeleton');
  }

  /**
   * Returns the component to its pre-load state: defaults, cleared timers, and an unfrozen widget.
   * @returns {void}
   */
  #reset() {
    this.settings = RATINGS_WIDGET_DEFAULT_SETTINGS;
    this.attempts = 0;
    this.widgetLoaded = false;
    this.#pendingRefreshHtml = null;

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }

    this.#clearShimmerDelay();
    this.#clearFallbackTimer();
    this.#disconnectWidgetObserver();
    this.#unfreezeWidget();
    this.removeAttribute('data-no-reviews');
    this.#skeleton = null;
  }

  /**
   * Freezes the widget container, to prevent Reviews.io functions from targeting it.
   * ! This is a workaround to prevent the widget from being updated when product cards around the page
   * ! are being loaded dynamically, which seems to call the ratingsSnippet function on all .ruk_rating_snippet
   * ! elements on the page, which in turn makes some widgets to get the wrong data.
   * @returns {void}
   */
  #freezeWidget() {
    this.#getWidgetContainer()?.classList.remove('ruk_rating_snippet');
  }

  /**
   * Unfreezes the widget container, to allow Reviews.io functions to target it again.
   * @returns {void}
   */
  #unfreezeWidget() {
    this.#getWidgetContainer()?.classList.add('ruk_rating_snippet');
  }

  /**
   * Handles a product select (variant change) event on the product page.
   * In-card variant changes are handled via `updatedCallback` (the card morphs this
   * element directly), so events originating from within a product-card are ignored here.
   *
   * @param {ProductSelectEvent} event - The product select event.
   * @returns {void}
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // `reload_on_variant_update: false` renders data-skip-node-update. Same-product
    // variant changes need no reload: data-sku already lists every variant sku/id.
    if (this.hasAttribute('data-skip-node-update')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        this.#scheduleRefresh(detail.html);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('IBD | Product Ratings | Event promise rejected:', error);
      });
  };

  /**
   * Handles an IBD product update event (combined-listing product switch).
   * Always refreshes — the product itself changed, so the SKUs and settings must update.
   *
   * @param {IbdProductUpdateEvent} event - The IBD product update event.
   * @returns {void}
   */
  #handleProductUpdated = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;
    this.#scheduleRefresh(html);
  };

  /**
   * Schedules a debounced widget refresh. One variant/product change can fire
   * multiple refresh signals (morph `updatedCallback`, productSelect promise,
   * ibd:product:updated); the debounce collapses them into one API request.
   *
   * @param {Document|null} [html] - Freshly fetched document with updated variant/product data.
   * @returns {void}
   */
  #scheduleRefresh(html = null) {
    if (html) this.#pendingRefreshHtml = html;
    if (this.refreshTimeout) clearTimeout(this.refreshTimeout);

    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = null;
      const pendingHtml = this.#pendingRefreshHtml;
      this.#pendingRefreshHtml = null;
      this.#refreshWidget(pendingHtml);
    }, REFRESH_DEBOUNCE_MS);
  }

  /**
   * Re-initialises the ratings widget, optionally from a freshly fetched document.
   * When `html` is provided, `data-settings`/`data-sku` are copied from it manually,
   * because the morph may have skipped this element (data-skip-node-update).
   *
   * @param {Document|null} html - The parsed HTML document with updated variant/product data.
   * @returns {void}
   */
  #refreshWidget(html) {
    const widgetContainer = this.#getWidgetContainer();
    if (!widgetContainer) return;

    if (html) {
      /** @type {HTMLElement|null} */
      const newElement = html.querySelector('ibd-product-ratings');
      const newContainer = newElement?.querySelector('[data-product-ratings-widget]');

      if (!newElement || !newContainer) {
        console.error('IBD | Product Ratings | #refreshWidget | Rating element not found in html data');
        return;
      }

      const newSettings = newElement.getAttribute('data-settings');
      if (newSettings) this.setAttribute('data-settings', newSettings);

      const newSku = newContainer.getAttribute('data-sku');
      if (newSku) widgetContainer.setAttribute('data-sku', newSku);
    }

    // Clear the Reviews.io bookkeeping and stale content so the container is
    // eligible for re-processing, then unfreeze it so ratingSnippet can match it.
    for (const attribute of REVIEWSIO_WIDGET_ATTRIBUTES) {
      widgetContainer.removeAttribute(attribute);
    }
    widgetContainer.replaceChildren();

    this.widgetLoaded = false;
    this.attempts = 0;
    this.#unfreezeWidget();
    this.#refreshSkeleton();
    this.#scheduleShimmer();
    this.#startLoading();
  }

  /**
   * On the PDP (not product cards), clicking the stars scrolls to the full
   * reviews widget. Reviews.io only does this when `anchorToReviewsWidget` is
   * true, which we do not set — `usePolaris` used to open a modal instead.
   * @param {Event} event
   * @returns {void}
   */
  #handleReviewsAnchor = (event) => {
    if (this.closest('product-card')) return;

    const reviews = document.getElementById('ReviewsWidget');
    if (!reviews) return;

    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return;
    if (event instanceof KeyboardEvent) event.preventDefault();

    /** @type {HTMLElement & { requestLoad?: () => void } | null} */
    const reviewsHost = document.querySelector('ibd-product-reviews');
    if (reviewsHost) {
      reviewsHost.setAttribute('data-ibd-request-load', '');
      reviewsHost.requestLoad?.();
    }

    reviews.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  /**
   * @returns {void}
   */
  #bindReviewsAnchor() {
    this.addEventListener('click', this.#handleReviewsAnchor);
    this.addEventListener('keydown', this.#handleReviewsAnchor);
  }

  /**
   * @returns {void}
   */
  #unbindReviewsAnchor() {
    this.removeEventListener('click', this.#handleReviewsAnchor);
    this.removeEventListener('keydown', this.#handleReviewsAnchor);
  }

  /**
   * Listens for variant changes on the nearest section and combined-listing product swaps on `document`.
   * @returns {void}
   */
  #bindEventListeners() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.addEventListener(IbdEvents.ibdProductUpdated, this.#handleProductUpdated);
  }

  /**
   * Removes the variant and combined-listing listeners added in `#bindEventListeners`.
   * @returns {void}
   */
  #unbindEventListeners() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.removeEventListener(IbdEvents.ibdProductUpdated, this.#handleProductUpdated);
  }
}

if (!customElements.get('ibd-product-ratings')) {
  customElements.define('ibd-product-ratings', IbdProductRatings);
}
